import React, { useRef, useLayoutEffect, useState, useMemo } from "react";
import { Timer, AlarmClock, CloudSun, Flame } from "lucide-react";
import { useZmanimData } from '../useZmanimData';

const MIN_PIXELS_PER_MINUTE = 0.3;

const C = {
  bg:        "#ffffff",
  bgAlt:     "#f9fafb",
  text:      "#111827",
  textMuted: "#6b7280",
  accent:    "#374151",
  border:    "#e5e7eb",
  emergency: "#dc2626",
  nowLine:   "#374151",
};

const FlexibleSpacer = ({ minutes }) => (
  <div style={{ flexGrow: minutes, minHeight: `${Math.max(0, minutes * MIN_PIXELS_PER_MINUTE)}px`, flexShrink: 0, flexBasis: '0px' }} />
);

/* SimpleItem — ללא עיגול icon, רק אייקון פשוט + שם + שעה */
const SimpleItem = ({ item, isCurrentTime, isPast }) => {
  const IconComponent = item.icon;
  return (
    <div style={{
      direction: 'rtl', opacity: isPast ? 0.45 : 1,
      padding: '8px 0',
      display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0,
      borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{ width: '44px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        <IconComponent style={{ color: isCurrentTime ? C.emergency : (isPast ? C.border : C.textMuted), width: '20px', height: '20px' }} />
      </div>
      <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: '48px', gap: '8px', minWidth: 0 }}>
        <span style={{ color: isCurrentTime ? C.emergency : (isPast ? C.textMuted : C.text), fontSize: '0.95em', fontWeight: isCurrentTime ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{item.title}</span>
        {item.subtitle && <span style={{ color: C.textMuted, fontSize: '0.75em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.subtitle}</span>}
        <span style={{ color: isCurrentTime ? C.emergency : C.textMuted, fontSize: '0.95em', fontWeight: 600, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{item.time}</span>
      </div>
    </div>
  );
};

/* AnnotationItem — ענייני היום כתגיות קומפקטיות */
const AnnotationItem = ({ item, isPast }) => {
  const pills = item.items || [{ value: item.title }];
  return (
    <div style={{ direction: 'rtl', opacity: isPast ? 0.45 : 1, display: 'flex', gap: '10px', alignItems: 'flex-start', flexShrink: 0 }}>
      <div style={{ width: '44px', flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: '5px' }}>
        <div style={{
          width: '8px', height: '8px', borderRadius: '50%',
          backgroundColor: C.accent,
          border: `2px solid ${C.bg}`,
          boxShadow: `0 0 0 1px ${C.border}`,
          zIndex: 10,
        }} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '2px 0' }}>
        {pills.map((pill, idx) => (
          <span key={idx} style={{
            display: 'inline-flex', alignItems: 'center',
            backgroundColor: C.bgAlt,
            border: `1px solid ${C.border}`,
            borderRadius: '8px',
            padding: '1px 8px',
            fontSize: '0.7em', fontWeight: 500, color: C.text,
            lineHeight: 1.3, maxWidth: '100%',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {pill.value || pill.label}
          </span>
        ))}
      </div>
    </div>
  );
};

/* HighlightedItem — כרטיס עם shadow בלבד, ללא border ימני צבעוני */
const HighlightedItem = ({ item, isCurrentTime, isPast }) => {
  const IconComponent = item.icon;
  const accentColor = item.accentColor || C.accent;
  return (
    <div style={{ direction: 'rtl', opacity: isPast ? 0.45 : 1, padding: '8px 0', display: 'flex', gap: '10px', flexShrink: 0 }}>
      <div style={{ width: '44px', flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: '8px' }}>
        <IconComponent style={{ color: isCurrentTime ? C.emergency : accentColor, width: '22px', height: '22px' }} />
      </div>
      <div style={{ flex: 1, marginLeft: '40px', marginRight: '40px' }}>
        <div style={{
          backgroundColor: C.bgAlt,
          border: isCurrentTime ? `1.5px solid ${C.emergency}` : `1px solid ${C.border}`,
          borderRadius: '12px',
          padding: '10px 20px',
          boxShadow: isCurrentTime ? `0 2px 8px ${C.emergency}20` : '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <span style={{ color: C.text, fontSize: '1.05em', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{item.title}</span>
            <span style={{ color: isCurrentTime ? C.emergency : C.accent, fontSize: '0.95em', fontWeight: 700, flexShrink: 0 }}>{item.time}</span>
          </div>
          {item.subtitle && <span style={{ color: C.textMuted, fontSize: '0.75em', display: 'block', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.subtitle}</span>}
        </div>
      </div>
    </div>
  );
};

/* NowIndicator — קו דק + שעה בטקסט, ללא pill */
const NowIndicator = ({ currentTimeStr }) => (
  <div style={{ direction: 'rtl', display: 'flex', alignItems: 'center', flexShrink: 0, position: 'relative', padding: '4px 0' }}>
    <div style={{ width: '44px', flexShrink: 0 }} />
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px', paddingLeft: '48px' }}>
      <div style={{ flex: 1, height: '1px', backgroundColor: C.nowLine }} />
      <span style={{ color: C.nowLine, fontSize: '1.4em', fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{currentTimeStr}</span>
      <div style={{ flex: 1, height: '1px', backgroundColor: C.nowLine }} />
    </div>
  </div>
);

const Minimal = ({ zmanimMap = {}, customScheduleItems = [], title = 'סדר היום', showTitle = true, hebrewDate = null, inyaneiItems = null, inyaneiDisplayMode = null }) => {
  const [layoutState, setLayoutState] = useState({ scale: 1, heightPercent: 100 });
  const containerRef = useRef(null);
  const innerListRef = useRef(null);

  const minimalIconMap = useMemo(() => ({
    sofZmanShmaMGA:           Timer,
    sofZmanShmaMGA16Point1:   Timer,
    sofZmanShma:              Timer,
    sofZmanTfillaMGA:         Timer,
    sofZmanTfillaMGA16Point1: Timer,
    sofZmanTfilla:            Timer,
    minchaGedola:             AlarmClock,
    minchaGedolaMGA:          AlarmClock,
    minchaKetana:             AlarmClock,
    minchaKetanaMGA:          AlarmClock,
    plagHaMincha:             Timer,
    sofZmanAchilatChametz:    Flame,
    sofZmanBiurChametz:       Flame,
    sofZmanAchilatChametzMGA: Flame,
    sofZmanBiurChametzMGA:    Flame,
    beinHaShmashos:           CloudSun,
  }), []);

  const { processedItems, nowItemIndex, currentTimeStr, currentItemMatch, currentTotalMinutes, dayWithHebrewDate } = useZmanimData({
    zmanimMap, customScheduleItems, hebrewDate,
    prayerColor: C.accent,
    lessonColor: C.textMuted,
    iconMap: minimalIconMap,
  });

  useLayoutEffect(() => {
    const calculateLayout = () => {
      if (!containerRef.current || !innerListRef.current) return;
      const containerHeight = containerRef.current.clientHeight;
      const el = innerListRef.current;
      el.style.width = '100%';
      el.style.height = 'auto';
      const contentHeight = el.scrollHeight;
      let newScale = 1;
      let newHeightPercent = 100;
      if (contentHeight > containerHeight) {
        newScale = containerHeight / contentHeight;
        el.style.width = `${100 / newScale}%`;
        const newContentHeight = el.scrollHeight;
        if (newContentHeight < contentHeight) newScale = Math.min(1, containerHeight / newContentHeight);
        newHeightPercent = 100 / newScale;
      }
      setLayoutState(prev => {
        if (Math.abs(prev.scale - newScale) < 0.001 && Math.abs(prev.heightPercent - newHeightPercent) < 0.1) {
          el.style.width = `${100 / prev.scale}%`;
          return prev;
        }
        return { scale: newScale, heightPercent: newHeightPercent };
      });
      el.style.width = `${100 / newScale}%`;
    };
    calculateLayout();
    const observer = new ResizeObserver(() => window.requestAnimationFrame(calculateLayout));
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [processedItems]);

  const showInyaneiSidebar = (inyaneiDisplayMode === 'sidebar' || inyaneiDisplayMode === 'combined') && inyaneiItems?.length > 0;
  const showInyaneiSection = inyaneiDisplayMode === 'section' && inyaneiItems?.length > 0;

  const gridRows = [];
  if (showTitle) gridRows.push('auto');
  if (showInyaneiSection) gridRows.push('auto');
  gridRows.push('1fr');

  return (
    <div style={{
      direction: 'rtl', width: '100%', height: '100%',
      backgroundColor: C.bg,
      overflow: 'hidden',
      display: 'grid',
      gridTemplateRows: gridRows.join(' '),
      gridTemplateColumns: showInyaneiSidebar ? '18% 1fr' : '1fr',
      fontSize: 'inherit',
    }}>

      {/* Header */}
      {showTitle && (
        <div style={{
          gridColumn: '1 / -1',
          backgroundColor: C.bg,
          borderBottom: `2px solid ${C.border}`,
          padding: '0.75rem 1.5rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '2px',
        }}>
          <span style={{ color: C.text, fontWeight: 700, fontSize: '20px' }}>{title}</span>
          <span style={{ color: C.textMuted, fontSize: '15px', fontWeight: 500 }}>{dayWithHebrewDate}</span>
        </div>
      )}

      {/* Inyanei Section */}
      {showInyaneiSection && (
        <div style={{
          gridColumn: '1 / -1',
          borderBottom: `1px solid ${C.border}`,
          backgroundColor: C.bgAlt,
          padding: '6px 1.5rem',
          display: 'flex', flexWrap: 'wrap', gap: '5px',
          alignItems: 'center', direction: 'rtl',
        }}>
          {inyaneiItems.map((item, idx) => {
            const Icon = item.IconComponent;
            return (
              <span key={idx} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                backgroundColor: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: '8px', padding: '2px 8px',
                fontSize: '0.7rem', fontWeight: 500, color: C.text,
                whiteSpace: 'nowrap',
              }}>
                <Icon size={11} style={{ color: C.textMuted, flexShrink: 0 }} />
                {item.value || item.label}
              </span>
            );
          })}
        </div>
      )}

      {/* Inyanei Sidebar */}
      {showInyaneiSidebar && (
        <div style={{
          overflowY: 'auto', padding: '0.75rem',
          backgroundColor: C.bgAlt,
          borderRight: `1px solid ${C.border}`,
          direction: 'rtl',
          display: 'flex', flexDirection: 'column', gap: '4px',
        }}>
          <div style={{ fontWeight: 600, color: C.text, fontSize: '0.78rem' }}>ענייני היום</div>
          {inyaneiItems.map((item, idx) => {
            const Icon = item.IconComponent;
            const isHigh = item.priority === 'high';
            return (
              <div key={`iny-${idx}`} style={{
                display: 'flex', alignItems: 'flex-start', gap: '5px',
                backgroundColor: C.bg,
                border: `1px solid ${isHigh ? `${C.accent}40` : C.border}`,
                borderRadius: '8px', padding: '4px 8px',
              }}>
                <Icon size={12} style={{ color: isHigh ? C.accent : C.textMuted, flexShrink: 0, marginTop: '2px' }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 500, color: C.text, lineHeight: 1.3, wordBreak: 'break-word' }}>
                    {item.value || item.label}
                  </div>
                  {item.detail && (
                    <div style={{ fontSize: '0.58rem', color: C.textMuted, lineHeight: 1.2, marginTop: '1px' }}>
                      {item.detail}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Main Timeline */}
      <div ref={containerRef} style={{ backgroundColor: C.bg, position: 'relative', overflow: 'hidden', height: '100%' }}>
        <div
          ref={innerListRef}
          style={{
            width: `${100 / layoutState.scale}%`,
            height: `${layoutState.heightPercent}%`,
            transform: `scale(${layoutState.scale})`,
            transformOrigin: 'top right',
            display: 'flex', flexDirection: 'column', minHeight: '100%',
            padding: '1rem 1.5rem', boxSizing: 'border-box',
          }}
        >
          {/* ציר דק מאוד */}
          <div style={{ position: 'absolute', top: 0, bottom: 0, width: '1px', right: '55px', backgroundColor: C.border, zIndex: 0 }} />

          {processedItems.map((item, index) => {
            const isCurrentTime = currentItemMatch?.index === item.index;
            const isNowIndicatorHere = index === nowItemIndex && !currentItemMatch;
            const isPast = item.realTime < currentTotalMinutes;
            const prevItemRealTime = index === 0 ? item.realTime : processedItems[index - 1].realTime;
            let diff = 0, diffBefore = 0, diffAfter = 0;
            if (isNowIndicatorHere) {
              diffBefore = Math.max(0, currentTotalMinutes - prevItemRealTime);
              diffAfter = Math.max(0, item.realTime - currentTotalMinutes);
            } else {
              diff = index === 0 ? 0 : Math.max(0, item.realTime - prevItemRealTime);
            }
            return (
              <div key={item.index} style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                {isNowIndicatorHere && (
                  <>
                    <FlexibleSpacer minutes={diffBefore} />
                    <NowIndicator currentTimeStr={currentTimeStr} />
                    <FlexibleSpacer minutes={diffAfter} />
                  </>
                )}
                {!isNowIndicatorHere && <FlexibleSpacer minutes={diff} />}
                {item.type === 'annotation'
                  ? <AnnotationItem item={item} isPast={isPast} />
                  : item.type === 'simple'
                    ? <SimpleItem item={item} isCurrentTime={isCurrentTime} isPast={isPast} />
                    : <HighlightedItem item={item} isCurrentTime={isCurrentTime} isPast={isPast} />}
              </div>
            );
          })}

          {nowItemIndex === processedItems.length && !currentItemMatch && (
            <>
              <div style={{ flexGrow: 1, minHeight: '20px' }} />
              <NowIndicator currentTimeStr={currentTimeStr} />
            </>
          )}
          <div style={{ minHeight: '5px', flexShrink: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default Minimal;
